/**
 * SCRUM-158 (P-8) — Zod schemas for the PriceAdjustment HTTP boundary.
 *
 * Endpoints covered (spec §10.1):
 *   #17 POST   /organizations/:slug/properties/:propertyId/price-adjustments   ApplyPriceAdjustment
 *   #18 POST   /organizations/:slug/price-adjustments/bulk                     BulkApplyPriceAdjustment
 *   #19 DELETE .../price-adjustments/:adjustmentId                             RemovePriceAdjustment (path-param only)
 *   #20 GET    /organizations/:slug/properties/:propertyId/price-adjustments   ListPriceAdjustments
 *
 * `ModifierSchema` is imported from season-group.ts (canonical definition).
 * `AppliesToBucket` domain values: 'weekday' | 'weekend' | 'both'.
 * Note: spec §10.1 table uses 'all' for the appliesToBucket field label, but
 * the domain VO (`applies-to-bucket.vo.ts`) uses 'both'. The HTTP boundary
 * uses 'all' per spec §6.3 to match client naming conventions; mappers translate
 * 'all' → 'both' before passing to the UC.
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/price-adjustment.spec.ts
 */
import { z } from 'zod';
import { isoLocalDateString, instantString, centsSchema } from './_shared.js';
import { ModifierSchema } from './season-group.js';
// ---------------------------------------------------------------------------
// ApplyPriceAdjustmentRequestSchema — endpoint #17
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/properties/:propertyId/price-adjustments body.
 * Semi-open date range: dateFrom < dateTo (dateTo is exclusive).
 */
export const ApplyPriceAdjustmentRequestSchema = z
    .object({
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    modifier: ModifierSchema,
    appliesToBucket: z.enum(['weekday', 'weekend', 'all']),
    reason: z.string().min(1).max(500),
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
});
// ---------------------------------------------------------------------------
// BulkApplyPriceAdjustmentRequestSchema — endpoint #18
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/price-adjustments/bulk body.
 *
 * Exactly ONE of `propertyIds` | `propertyGroupId` must be present (XOR).
 *   - propertyIds mode  — explicit list, min 1 max 500 (backward-compat)
 *   - propertyGroupId mode — DS-014 S4: resolve group members via the
 *     PropertyGroupMemberResolutionPort before running the apply path.
 *
 * Both present → reject (security: a group is NOT an authorization bypass).
 * Neither present → reject (requires at least one targeting mode).
 */
export const BulkApplyPriceAdjustmentRequestSchema = z
    .object({
    propertyIds: z.array(z.string().uuid()).min(1).max(500).optional(),
    propertyGroupId: z.string().uuid().optional(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    modifier: ModifierSchema,
    appliesToBucket: z.enum(['weekday', 'weekend', 'all']),
    reason: z.string().min(1).max(500),
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
})
    .refine((req) => {
    const hasPropertyIds = req.propertyIds !== undefined;
    const hasPropertyGroupId = req.propertyGroupId !== undefined;
    // Exactly one must be present
    return hasPropertyIds !== hasPropertyGroupId;
}, {
    message: 'Exactly one of propertyIds or propertyGroupId must be provided, not both and not neither',
    path: ['propertyGroupId'],
});
// ---------------------------------------------------------------------------
// Response schemas — endpoints #17, #18, #20
// ---------------------------------------------------------------------------
/**
 * ApplyPriceAdjustmentResponseSchema — single-property apply response (endpoint #17).
 */
export const ApplyPriceAdjustmentResponseSchema = z.object({
    priceAdjustmentIds: z.array(z.string().uuid()),
    appliedDatesCount: z.number().int().nonnegative(),
    skippedBlockedDatesCount: z.number().int().nonnegative(),
});
/**
 * BulkApplyEntrySchema — per-property result entry in the bulk response.
 */
export const BulkApplyEntrySchema = z.object({
    propertyId: z.string().uuid(),
    outcome: z.enum(['applied', 'partial', 'skipped']),
    priceAdjustmentIds: z.array(z.string().uuid()),
    appliedDatesCount: z.number().int().nonnegative(),
    skippedBlockedDatesCount: z.number().int().nonnegative(),
    skippedConflictDatesCount: z.number().int().nonnegative(),
    reason: z
        .enum([
        'fully-blocked',
        'fully-conflict',
        'property-not-found',
        'conflict',
        'external-sync-enabled',
    ])
        .optional(),
});
/**
 * BulkApplyPriceAdjustmentResponseSchema — bulk apply response (endpoint #18).
 */
export const BulkApplyPriceAdjustmentResponseSchema = z.object({
    results: z.array(BulkApplyEntrySchema),
});
/**
 * PriceAdjustmentItemSchema — one entry in the list response.
 */
export const PriceAdjustmentItemSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    modifier: ModifierSchema,
    appliesToBucket: z.enum(['weekday', 'weekend', 'all']),
    reason: z.string(),
    createdAt: instantString,
});
/**
 * PriceAdjustmentListResponseSchema — GET .../price-adjustments response (endpoint #20).
 */
export const PriceAdjustmentListResponseSchema = z.object({
    items: z.array(PriceAdjustmentItemSchema),
});
// Re-export centsSchema for consumers that want a typed Cents field
export { centsSchema };
//# sourceMappingURL=price-adjustment.js.map